/**
 * PintsSettings - Pints & Pegs Drinking Options UI System
 * Encapsulates drinking configurations, row rendering, toggle handlers,
 * and badge updates across all 8 drinking spaces.
 */

export const PINTS_DRINKING_DEFS = Object.freeze([
  {
    key: 'youDrink',
    name: 'You Drink',
    chkId: 'optYouDrink',
    countId: 'optYouDrinkCount',
    pegsId: 'optYouDrinkPegs',
    subId: 'youDrinkSub',
    configId: 'youDrinkConfig',
    rowPrefix: 'youDrinkRow',
    badgePrefix: 'youDrinkBadge',
    holeIdPrefix: 'optYouDrink',
    type: 'you-drink',
    desc: 'Landing here requires the player to take a drink',
    defaultHoles: [15, 45, 75, 105]
  },
  {
    key: 'guysDrink',
    name: 'Guys Drink',
    chkId: 'optGuysDrink',
    countId: 'optGuysDrinkCount',
    pegsId: 'optGuysDrinkPegs',
    subId: 'guysDrinkSub',
    configId: 'guysDrinkConfig',
    rowPrefix: 'guysDrinkRow',
    badgePrefix: 'guysDrinkBadge',
    holeIdPrefix: 'optGuysDrink',
    type: 'guys-drink',
    desc: 'Landing here requires all male players to take a drink',
    defaultHoles: [25, 55, 85, 115]
  },
  {
    key: 'galsDrink',
    name: 'Gals Drink',
    chkId: 'optGalsDrink',
    countId: 'optGalsDrinkCount',
    pegsId: 'optGalsDrinkPegs',
    subId: 'galsDrinkSub',
    configId: 'galsDrinkConfig',
    rowPrefix: 'galsDrinkRow',
    badgePrefix: 'galsDrinkBadge',
    holeIdPrefix: 'optGalsDrink',
    type: 'gals-drink',
    desc: 'Landing here requires all female players to take a drink',
    defaultHoles: [35, 65, 95, 105]
  },
  {
    key: 'leftDrink',
    name: 'Left Drinks',
    chkId: 'optLeftDrink',
    countId: 'optLeftDrinkCount',
    pegsId: 'optLeftDrinkPegs',
    subId: 'leftDrinkSub',
    configId: 'leftDrinkConfig',
    rowPrefix: 'leftDrinkRow',
    badgePrefix: 'leftDrinkBadge',
    holeIdPrefix: 'optLeftDrink',
    type: 'left-drink',
    desc: 'Landing here requires the player to your left to take a drink',
    defaultHoles: [20, 50, 80, 110]
  },
  {
    key: 'rightDrink',
    name: 'Right Drinks',
    chkId: 'optRightDrink',
    countId: 'optRightDrinkCount',
    pegsId: 'optRightDrinkPegs',
    subId: 'rightDrinkSub',
    configId: 'rightDrinkConfig',
    rowPrefix: 'rightDrinkRow',
    badgePrefix: 'rightDrinkBadge',
    holeIdPrefix: 'optRightDrink',
    type: 'right-drink',
    desc: 'Landing here requires the player to your right to take a drink',
    defaultHoles: [40, 70, 100, 120]
  },
  {
    key: 'everyoneDrink',
    name: 'Everyone Drinks',
    chkId: 'optEveryoneDrink',
    countId: 'optEveryoneDrinkCount',
    pegsId: 'optEveryoneDrinkPegs',
    subId: 'everyoneDrinkSub',
    configId: 'everyoneDrinkConfig',
    rowPrefix: 'everyoneDrinkRow',
    badgePrefix: 'everyoneDrinkBadge',
    holeIdPrefix: 'optEveryoneDrink',
    type: 'everyone-drink',
    desc: 'Landing here requires all players to take a drink',
    defaultHoles: [30, 60, 90, 120]
  },
  {
    key: 'everyoneButYouDrink',
    name: 'Everyone But You',
    chkId: 'optEveryoneButYouDrink',
    countId: 'optEveryoneButYouDrinkCount',
    pegsId: 'optEveryoneButYouDrinkPegs',
    subId: 'everyoneButYouDrinkSub',
    configId: 'everyoneButYouDrinkConfig',
    rowPrefix: 'everyoneButYouDrinkRow',
    badgePrefix: 'everyoneButYouDrinkBadge',
    holeIdPrefix: 'optEveryoneButYouDrink',
    type: 'everyone-but-you-drink',
    desc: 'Landing here requires all players except you to take a drink',
    defaultHoles: [10, 50, 90, 110]
  },
  {
    key: 'pickWhoDrinks',
    name: 'Pick Who Drinks',
    chkId: 'optPickWhoDrinks',
    countId: 'optPickWhoDrinksCount',
    pegsId: 'optPickWhoDrinksPegs',
    subId: 'pickWhoDrinksSub',
    configId: 'pickWhoDrinksConfig',
    rowPrefix: 'pickWhoDrinksRow',
    badgePrefix: 'pickWhoDrinksBadge',
    holeIdPrefix: 'optPickWhoDrinks',
    type: 'pick-who-drinks',
    desc: 'Landing here allows you to choose who takes a drink',
    defaultHoles: [25, 55, 85, 115]
  }
]);

export class PintsSettings {
  static getDrinkingDefs() {
    return PINTS_DRINKING_DEFS;
  }

  static getDrinkingDef(key) {
    return PINTS_DRINKING_DEFS.find(d => d.key === key) || null;
  }

  static toggleDrinkingConfig(key, checked, options = {}) {
    const def = PintsSettings.getDrinkingDef(key);
    if (!def) return;
    if (typeof document !== 'undefined') {
      const panel = document.getElementById(def.configId);
      if (panel) panel.style.display = checked ? 'flex' : 'none';
    }

    PintsSettings.updateDrinkingRows(key, options);
    for (let i = 1; i <= 4; i++) {
      PintsSettings.onDrinkingFieldChange(key, i, true, options);
    }

    if (checked) {
      if (typeof options.enforceClashNoOverlap === 'function') options.enforceClashNoOverlap();
    } else {
      if (typeof options.updateAllClashDropdowns === 'function') options.updateAllClashDropdowns();
    }

    if (typeof options.saveSettings === 'function') {
      options.saveSettings();
    }
  }

  static updateDrinkingRows(key, options = {}) {
    const def = PintsSettings.getDrinkingDef(key);
    if (!def) return;
    if (typeof document !== 'undefined') {
      const countEl = document.getElementById(def.countId);
      const count = countEl ? parseInt(countEl.value, 10) : 2;
      for (let i = 1; i <= 4; i++) {
        const row = document.getElementById(def.rowPrefix + i);
        if (row) row.style.display = i <= count ? 'flex' : 'none';
      }
    }
    if (typeof options.enforceClashNoOverlap === 'function') {
      options.enforceClashNoOverlap();
    }
    if (typeof options.saveSettings === 'function') {
      options.saveSettings();
    }
  }

  static onDrinkingPegsChange(key, options = {}) {
    const def = PintsSettings.getDrinkingDef(key);
    if (!def) return;
    let pegs = 1;
    if (typeof document !== 'undefined') {
      const pegsEl = document.getElementById(def.pegsId);
      pegs = pegsEl ? parseInt(pegsEl.value, 10) : 1;
      const sub = document.getElementById(def.subId);
      if (sub) {
        sub.textContent = `${pegs === 1 ? '1 peg' : pegs + ' pegs'} · ${def.desc}`;
      }
    }
    if (options.settings) {
      options.settings[def.key + 'Pegs'] = pegs;
    }
    for (let i = 1; i <= 4; i++) {
      PintsSettings.onDrinkingFieldChange(key, i, true, options);
    }
    if (typeof options.enforceClashNoOverlap === 'function') {
      options.enforceClashNoOverlap();
    }
    if (typeof options.syncAllClashPlacementButtons === 'function') {
      options.syncAllClashPlacementButtons();
    }
    if (typeof options.saveSettings === 'function') {
      options.saveSettings();
    }
  }

  static onDrinkingFieldChange(key, idx, skipSave = false, options = {}) {
    const def = PintsSettings.getDrinkingDef(key);
    if (!def) return;
    const fieldId = `${def.holeIdPrefix}${idx}Hole`;
    if (typeof options.enforceClashNoOverlap === 'function') {
      options.enforceClashNoOverlap(fieldId);
    }

    if (typeof document !== 'undefined') {
      const hEl = document.getElementById(fieldId);
      const bEl = document.getElementById(`${def.badgePrefix}${idx}`);
      const pegsEl = document.getElementById(def.pegsId);
      const pegs = parseInt(pegsEl?.value || (options.settings && options.settings[def.key + 'Pegs']) || 1, 10);

      if (hEl && bEl) {
        const h = parseInt(hEl.value, 10) || def.defaultHoles[idx - 1] || 15;
        const streetName = typeof options.getStreetOrTurnName === 'function' ? options.getStreetOrTurnName(h) : '';
        if (pegs > 1) {
          const endH = Math.min(121, h + pegs - 1);
          bEl.textContent = `${streetName ? streetName + ' · ' : ''}Holes ${h}–${endH} (${pegs} Pegs)`;
        } else {
          bEl.textContent = `${streetName ? streetName + ' · ' : ''}Hole ${h}`;
        }
      }
    }

    if (!skipSave && typeof options.saveSettings === 'function') {
      options.saveSettings();
    }
  }

  static getDrinkingConfigs(key, settings = {}) {
    const def = PintsSettings.getDrinkingDef(key);
    if (!def || !settings[def.chkId]) return [];
    const count = Math.min(4, Math.max(1, settings[def.key + 'Count'] || 2));
    const pegs = Math.min(5, Math.max(1, parseInt(settings[def.key + 'Pegs'], 10) || 1));
    const holes = settings[def.key + 'Holes'] || def.defaultHoles;
    const list = [];
    for (let i = 0; i < count; i++) {
      const h = holes[i] != null ? holes[i] : (def.defaultHoles[i] || (15 + i * 30));
      list.push({
        id: i + 1,
        hole: h,
        start: h,
        end: h + pegs - 1,
        pegs: pegs,
        type: def.type,
        label: `${def.name} ${i + 1}`
      });
    }
    return list;
  }
}
